import { useState, type ReactElement } from 'react';
import { ActionIcon, Button, Divider, Group, Popover, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { Check, Pencil, Pin, PinOff, RotateCcw, Trash2 } from 'lucide-react';
import type { MegaGridView } from './MegaGridTypes';

interface MegaGridSavedViewsProps {
    views: MegaGridView[];
    activeViewId: string | null;
    onApply: (view: MegaGridView) => void;
    onSave: (name: string) => void;
    onRename: (id: string, name: string) => void;
    onDelete: (id: string) => void;
    onTogglePin: (id: string) => void;
    onReset: () => void;
}

/** Saved-view management: pinned views as one-click toolbar chips, plus the Views dropdown with save / rename / pin / delete. */
export default function MegaGridSavedViews(props: MegaGridSavedViewsProps): ReactElement {
    const { views, activeViewId, onApply, onSave, onRename, onDelete, onTogglePin, onReset } = props;
    const [name, setName] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');

    const activeName = views.find((v) => v.id === activeViewId)?.name;

    const commitSave = (): void => {
        const trimmed = name.trim();
        if (!trimmed) return;
        onSave(trimmed);
        setName('');
    };

    const commitRename = (id: string): void => {
        const trimmed = editName.trim();
        if (trimmed) onRename(id, trimmed);
        setEditingId(null);
    };

    const pinnedViews = views.filter((view) => view.pinned === true);
    const unpinnedViews = views.filter((view) => view.pinned !== true);

    const viewRow = (view: MegaGridView): ReactElement =>
        editingId === view.id ? (
            <Group key={view.id} gap={4} wrap="nowrap">
                <TextInput
                    size="xs"
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.currentTarget.value)}
                    onKeyDown={(e) => e.key === 'Enter' && commitRename(view.id)}
                    style={{ flex: 1 }}
                />
                <ActionIcon size="sm" variant="subtle" onClick={() => commitRename(view.id)} aria-label="Save name">
                    <Check size={14} />
                </ActionIcon>
            </Group>
        ) : (
            <Group key={view.id} gap={4} wrap="nowrap">
                <UnstyledButton
                    onClick={() => onApply(view)}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        padding: '4px 6px',
                        borderRadius: 'var(--mantine-radius-sm)',
                        background: view.id === activeViewId ? 'var(--bm-color-zinc-hover)' : undefined,
                    }}
                >
                    <Text size="xs" fw={view.id === activeViewId ? 700 : 400} truncate>
                        {view.name}
                    </Text>
                </UnstyledButton>
                <Tooltip label={view.pinned === true ? 'Unpin' : 'Pin to top'} withArrow>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color={view.pinned === true ? 'brand' : 'gray'}
                        onClick={() => onTogglePin(view.id)}
                        aria-label={view.pinned === true ? `Unpin ${view.name}` : `Pin ${view.name}`}
                    >
                        {view.pinned === true ? <PinOff size={13} /> : <Pin size={13} />}
                    </ActionIcon>
                </Tooltip>
                <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                        setEditingId(view.id);
                        setEditName(view.name);
                    }}
                    aria-label="Rename view"
                >
                    <Pencil size={13} />
                </ActionIcon>
                <ActionIcon size="sm" variant="subtle" color="red" onClick={() => onDelete(view.id)} aria-label="Delete view">
                    <Trash2 size={13} />
                </ActionIcon>
            </Group>
        );

    return (
        <Popover position="bottom-end" withArrow shadow="md" width={280}>
            <Popover.Target>
                <Button size="xs" variant="default">
                    {activeName ?? 'Views'}
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                        Saved views
                    </Text>
                    {views.length === 0 && (
                        <Text size="xs" c="dimmed">
                            No saved views yet.
                        </Text>
                    )}
                    <Stack gap={2} mah={220} style={{ overflowY: 'auto' }}>
                        {pinnedViews.length > 0 && (
                            <Group gap={4} wrap="nowrap" mb={2}>
                                <Pin size={11} style={{ color: 'var(--bm-color-text-dimmed)' }} />
                                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                                    Pinned
                                </Text>
                            </Group>
                        )}
                        {pinnedViews.map(viewRow)}
                        {pinnedViews.length > 0 && unpinnedViews.length > 0 && <Divider my={4} />}
                        {unpinnedViews.map(viewRow)}
                    </Stack>
                    <Divider />
                    <Group gap={4} wrap="nowrap">
                        <TextInput
                            size="xs"
                            placeholder="Save current view as…"
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            onKeyDown={(e) => e.key === 'Enter' && commitSave()}
                            style={{ flex: 1 }}
                        />
                        <Button size="compact-xs" onClick={commitSave} disabled={!name.trim()}>
                            Save
                        </Button>
                    </Group>
                    <Button size="compact-xs" variant="subtle" color="gray" leftSection={<RotateCcw size={13} />} onClick={onReset}>
                        Reset to default
                    </Button>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}
